import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Checkbox,
  IconButton,
  Tooltip,
  Box,
  InputAdornment,
  TextField,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  ListItemText,
  Badge,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Button from "../Button/Button";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterBadge from "@mui/icons-material/Badge";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";

import {
  StyledTableCell,
  TableToolbar,
  SearchField,
  ActionButton,
  StyledTableRow,
  HeaderSearchInput,
  TableTitle,
  tableColors,
  SortIconWrapper,
  TableHeader,
  TableHeaderCell,
  ArrowDropDown,
  DataCell,
  FilterContainer,
  FilterButton,
  FilterPopoverContent,
  FilterHeader,
  FilterOptions,
  TableWrapper,
  SearchAndFilterContainer,
  FilterActions,
  FilterCount,
  ClearAllButton,
} from "./TableStyles";

const getColumnWidth = (columnId) => {
  const widths = {
    actions: 100, // Width for actions column
    checkbox: 48, // Width for selection checkbox
  };
  return widths[columnId] || 200; // Default width of 200 for undefined columns
};

const getFixedPosition = (
  columnId,
  fixedColumns,
  allColumns,
  showSelection
) => {
  const { left, right } = fixedColumns;

  if (left.includes(columnId)) {
    let position = 0;
    // Add selection checkbox width if it's shown
    if (showSelection) {
      position += 48;
    }
    // Calculate position based on previous columns
    for (let i = 0; i < left.indexOf(columnId); i++) {
      position += getColumnWidth(left[i]);
    }
    return { position: "left", value: position };
  }

  if (right.includes(columnId)) {
    let position = 0;
    for (let i = right.length - 1; i > right.indexOf(columnId); i--) {
      position += getColumnWidth(right[i]);
    }
    return { position: "right", value: position };
  }

  return { position: null, value: null };
};

const getSearchWidth = (value) => {
  const baseWidth = 300;
  const charWidth = 8; // Approximate width per character
  const padding = 64; // Account for icons and padding
  const calculatedWidth = value.length * charWidth + padding;
  return Math.max(baseWidth, Math.min(600, calculatedWidth)); // Between 300 and 600px
};

export const CustomTable = ({
  columns,
  data,
  onEdit,
  onDelete,
  onAdd,
  showActions = true,
  showAddButton = true,
  showSearch = true,
  showFilters = true,
  showSelection = false,
  showColumnVisibility = false,
  pageSize = 10,
  addButtonText = "Add New",
  addButtonIcon = <PersonAddIcon />,
  disabled = false,
  onSelectionChange,
  defaultHiddenColumns = [],
  stickyHeader = true,
  fixedColumns = { left: [], right: [] },
  actions = [],
  title = "",
  subtitle = "",
  alignment = "left",
  fontSize = "13px",
  columnFilters = [],
  compact = false,
  // Optional: restrict which columns are searched by global search
  searchKeys,
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(pageSize);
  const [selected, setSelected] = React.useState([]);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [visibleColumns, setVisibleColumns] = React.useState(
    columns
      .map((col) => col.id)
      .filter((id) => !defaultHiddenColumns.includes(id))
  );
  const [sortConfig, setSortConfig] = React.useState({
    key: null,
    direction: "asc",
  });
  const [globalSearchValue, setGlobalSearchValue] = React.useState("");
  const [filterValues, setFilterValues] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleGlobalSearch = (event) => {
    const value = event.target.value;
    setGlobalSearchValue(value);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = data.map((row) => row.id);
      setSelected(newSelected);
      onSelectionChange?.(newSelected);
    } else {
      setSelected([]);
      onSelectionChange?.([]);
    }
  };

  const handleRowSelect = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
    onSelectionChange?.(newSelected);
  };

  const handleSort = (columnId) => {
    setSortConfig((prev) => ({
      key: columnId,
      direction:
        prev.key === columnId && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleFilterChange = (columnId, values) => {
    setFilterValues((prev) => ({
      ...prev,
      [columnId]: values,
    }));
  };

  const getUniqueColumnValues = (columnId) => {
    const filter = columnFilters.find((f) => f.id === columnId);
    return [
      ...new Set(
        data.map((item) => {
          const value = item[columnId];
          return filter?.render ? filter.render(value) : value;
        })
      ),
    ].filter(Boolean);
  };

  const filteredData = React.useMemo(() => {
    let filtered = data;

    // Apply column filters
    Object.entries(filterValues).forEach(([columnId, values]) => {
      if (values?.length) {
        const filter = columnFilters.find((f) => f.id === columnId);
        filtered = filtered.filter((row) => {
          const rowValue = filter?.render
            ? filter.render(row[columnId])
            : row[columnId];
          return values.includes(rowValue);
        });
      }
    });

    // Apply global search
    if (globalSearchValue) {
      const searchTerm = globalSearchValue.toLowerCase();
      filtered = filtered.filter((row) => {
        // If specific keys are provided, search only in those
        if (Array.isArray(searchKeys) && searchKeys.length > 0) {
          return searchKeys.some((key) => {
            const value = row[key];
            return value !== undefined &&
              String(value).toLowerCase().includes(searchTerm);
          });
        }

        // Default: search across all values in the row
        return Object.values(row)
          .join(" ")
          .toLowerCase()
          .includes(searchTerm);
      });
    }

    return filtered;
  }, [data, filterValues, globalSearchValue, columnFilters, searchKeys]);

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (sortConfig.direction === "asc") {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });
  }, [filteredData, sortConfig]);

  const getColumnSx = (columnId, isHeader = false) => {
    const { position, value } = getFixedPosition(
      columnId,
      fixedColumns,
      columns,
      showSelection
    );
    return (theme) => {
      let sx = {};
      if (isHeader) {
        sx.position = "sticky";
        sx.top = 0;
        sx.zIndex = 1200;
        sx.backgroundColor = tableColors.header.background;
        sx.fontWeight = 600;
      }
      if (position) {
        sx.position = "sticky";
        sx[position] = value;
        // If header, keep zIndex high, else lower
        sx.zIndex = isHeader
          ? position === "left"
            ? 1200
            : 1000
          : position === "left"
          ? 1100
          : 900;
      }
      if (!isHeader && !position) {
        sx.backgroundColor = tableColors.row.even;
      }
      return sx;
    };
  };

  const getSelectionColumnSx = (isHeader) => (theme) => ({
    position: "sticky",
    left: 0,
    zIndex: isHeader ? 1300 : 1100,
    backgroundColor: isHeader
      ? tableColors.header.background
      : tableColors.row.even,
    width: 48,
    minWidth: 48,
  });

  const getActionsColumnSx = (isHeader) => (theme) => ({
    position: "sticky",
    right: 0,
    zIndex: isHeader ? 1300 : 1100,
    backgroundColor: isHeader
      ? tableColors.header.background
      : tableColors.row.even,
    width: 100,
    minWidth: 100,
  });

  const renderTableToolbarWithoutSearch = () => (
    <TableToolbar>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 2,
        }}
      >
        {title && (
          <Box sx={{ flex: 1 }}>
            <TableTitle>{title}</TableTitle>
            {subtitle && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </TableToolbar>
  );

  const renderTableToolbarWithFilters = () => (
    <SearchAndFilterContainer>
      {showSearch && (
        <SearchField
          placeholder="Search..."
          value={globalSearchValue}
          onChange={handleGlobalSearch}
          fullWidth={isMobile}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: globalSearchValue && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setGlobalSearchValue("")}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}

      {showFilters && columnFilters.length > 0 && (
        <FilterContainer>
          {columnFilters.map((filter) => {
            const activeFilterValues = filterValues[filter.id] || [];
            const hasActiveFilters = activeFilterValues.length > 0;

            return (
              <FilterButton
                key={filter.id}
                active={activeFilter === filter.id}
                hasFilters={hasActiveFilters}
                onClick={() =>
                  setActiveFilter(activeFilter === filter.id ? null : filter.id)
                }
                aria-expanded={activeFilter === filter.id}
                aria-haspopup="true"
                data-filter-id={filter.id}
              >
                {filter.icon && (
                  <span className="filter-icon">{filter.icon}</span>
                )}
                <span>{filter.label}</span>
                {hasActiveFilters && (
                  <>
                    <FilterCount>{activeFilterValues.length}</FilterCount>
                    <CloseIcon
                      className="clear-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilterValues((prev) => ({
                          ...prev,
                          [filter.id]: [],
                        }));
                      }}
                    />
                  </>
                )}
                <KeyboardArrowDownIcon className="send-icon" />
              </FilterButton>
            );
          })}

          {/* Add Clear All button if any filters are active */}
          {Object.values(filterValues).some((values) => values.length > 0) && (
            <ClearAllButton
              variant="outlined"
              onClick={() => setFilterValues({})}
              startIcon={<CloseIcon />}
            >
              Clear All
            </ClearAllButton>
          )}
        </FilterContainer>
      )}
    </SearchAndFilterContainer>
  );

  const renderActions = (row) => {
    // Handle actions as either a function or an array
    const actionItems = typeof actions === "function" ? actions(row) : actions;

    return (
      <TableCell align="right">
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          {actionItems?.map((action, index) => (
            <Tooltip key={index} title={action.label}>
              <IconButton
                size="small"
                onClick={() => action.onClick(row)}
                sx={{
                  color:
                    action.color === "error"
                      ? (theme) => theme.palette.error.main
                      : (theme) => theme.palette.primary.main,
                  padding: "4px",
                  "&:hover": {
                    backgroundColor:
                      action.color === "error"
                        ? (theme) =>
                            theme.palette.error.lighter ||
                            "rgba(244, 67, 54, 0.08)"
                        : (theme) =>
                            theme.palette.primary.lighter ||
                            "rgba(33, 150, 243, 0.08)",
                  },
                  ...action.sx,
                }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      </TableCell>
    );
  };

  return (
    <TableWrapper compact={compact}>
      {/* Title and Add Button Section */}
      <TableToolbar compact={compact}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: 2,
          }}
        >
          {title && (
            <Box sx={{ flex: 1 }}>
              <TableTitle compact={compact}>
                <h1>{title}</h1>
              </TableTitle>
              {subtitle && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: compact ? "0.7rem" : "0.75rem" }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          )}
          {showAddButton && (
            <Button
              variant="contained"
              onClick={onAdd}
              startIcon={addButtonIcon}
              size="small"
            >
              {addButtonText}
            </Button>
          )}
        </Box>
      </TableToolbar>

      {/* Search and Filters Section */}
      {(showSearch || showFilters) && (
        <SearchAndFilterContainer compact={compact}>
          {showSearch && (
            <SearchField
              compact={compact}
              placeholder="Search..."
              value={globalSearchValue}
              onChange={handleGlobalSearch}
              fullWidth={isMobile}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: globalSearchValue && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setGlobalSearchValue("")}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {showFilters && columnFilters.length > 0 && (
            <FilterContainer>
              {columnFilters.map((filter) => {
                const activeFilterValues = filterValues[filter.id] || [];
                const hasActiveFilters = activeFilterValues.length > 0;

                return (
                  <FilterButton
                    key={filter.id}
                    active={activeFilter === filter.id}
                    hasFilters={hasActiveFilters}
                    onClick={() =>
                      setActiveFilter(
                        activeFilter === filter.id ? null : filter.id
                      )
                    }
                    aria-expanded={activeFilter === filter.id}
                    aria-haspopup="true"
                    data-filter-id={filter.id}
                  >
                    {filter.icon && (
                      <span className="filter-icon">{filter.icon}</span>
                    )}
                    <span>{filter.label}</span>
                    {hasActiveFilters && (
                      <>
                        <FilterCount>{activeFilterValues.length}</FilterCount>
                        <CloseIcon
                          className="clear-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterValues((prev) => ({
                              ...prev,
                              [filter.id]: [],
                            }));
                          }}
                        />
                      </>
                    )}
                    <KeyboardArrowDownIcon className="send-icon" />
                  </FilterButton>
                );
              })}

              {/* Add Clear All button if any filters are active */}
              {Object.values(filterValues).some(
                (values) => values.length > 0
              ) && (
                <ClearAllButton
                  variant="outlined"
                  onClick={() => setFilterValues({})}
                  startIcon={<CloseIcon />}
                >
                  Clear All
                </ClearAllButton>
              )}
            </FilterContainer>
          )}
        </SearchAndFilterContainer>
      )}

      {/* Table Section */}
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          flex: compact ? 1 : "none",
          minHeight: compact ? 0 : "auto",
          maxHeight: compact
            ? "none"
            : {
                md: "clamp(240px, 45vh, 60vh)",
                lg: "clamp(240px, 50vh, 70vh)",
              },
          "@media (min-width:1441px)": compact
            ? {}
            : { maxHeight: "clamp(240px, 60vh, 85vh)" },
          overflowY: "auto",
          "& .MuiTableCell-root": {
            borderColor: (theme) => theme.palette.divider,
          },
        }}
      >
        <Table stickyHeader={true}>
          <TableHead>
            <TableRow>
              {showSelection && (
                <StyledTableCell
                  padding="checkbox"
                  sx={getSelectionColumnSx(true)}
                >
                  <Checkbox
                    indeterminate={
                      selected.length > 0 && selected.length < sortedData.length
                    }
                    checked={
                      selected.length > 0 &&
                      selected.length === sortedData.length
                    }
                    onChange={handleSelectAllClick}
                    disabled={disabled}
                  />
                </StyledTableCell>
              )}
              {columns
                .filter((column) => visibleColumns.includes(column.id))
                .map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align="left"
                    fontSize={fontSize}
                    data-field={column.id}
                    column={column}
                    onClick={() => handleSort(column.id)}
                    sx={getColumnSx(column.id, true)}
                  >
                    <div className="header-content">
                      {column.label}
                      {/* <SortIconWrapper 
                        active={sortConfig.key === column.id}
                        direction={sortConfig.direction}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </SortIconWrapper> */}
                    </div>
                  </StyledTableCell>
                ))}
              {showActions && (
                <StyledTableCell align="left" sx={getActionsColumnSx(true)}>
                  ACTIONS
                </StyledTableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <StyledTableRow
                  key={row.id || index}
                  selected={selected.includes(row.id)}
                  hover
                  fontSize={fontSize}
                >
                  {showSelection && (
                    <TableCell
                      padding="checkbox"
                      sx={getSelectionColumnSx(false)}
                    >
                      <Checkbox
                        checked={selected.includes(row.id)}
                        onChange={() => handleRowSelect(row.id)}
                        disabled={disabled}
                      />
                    </TableCell>
                  )}
                  {columns
                    .filter((column) => visibleColumns.includes(column.id))
                    .map((column) => (
                      <DataCell
                        key={column.id}
                        //align={alignment}
                        fontSize={fontSize}
                        data-field={column.id}
                        data-label={column.label}
                        column={column}
                        sx={
                          {
                          ...getColumnSx(column.id, false),
                          textAlign: 'left !important',
                          '& *': { textAlign: 'left !important' }
                          }
                        }
                      >
                        <div className="cell-content">
                          {column.render
                            ? column.render(row[column.id], row)
                            : row[column.id]}
                        </div>
                      </DataCell>
                    ))}
                  {showActions && (
                    <StyledTableCell
                      align="left"
                      data-field="actions"
                      sx={getActionsColumnSx(false)}
                    >
                      {renderActions(row)}
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Section */}
      <Box
        sx={{
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          background: "#eeeeee",
          minHeight:0,
          position: 'sticky', 
          bottom: 0, left: 0, right: 0, zIndex: 2 
        }}
      >
        <TablePagination
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            "& .MuiTablePagination-toolbar": {
              flexWrap: "wrap",
              gap: 0.5,
              minHeight: 32,
              padding: "2px 10px",
              justifyContent: { xs: "center", sm: "flex-end" },
            },
            "& .MuiTablePagination-selectLabel": {
              fontSize: "0.7rem",
              margin: 0,
              color: "#444",
              fontWeight: 500,
              lineHeight: 0,
              display: "flex",
              alignItems: "center",
            },
            "& .MuiTablePagination-displayedRows": {
              fontSize: "0.7rem",
              margin: 0,
              color: "#444",
              fontWeight: 500,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
            },
            "& .MuiTablePagination-select": {
              fontSize: "0.7rem",
              padding: "1px 2px 1px 2px",
              fontWeight: 500,
              color: "#3F49A7"
            },
            "& .MuiTablePagination-selectIcon": {
              fontSize: "0.9rem",
            },
            "& .MuiTablePagination-actions": {
             
              display: "flex",
              alignItems: "center",
            },
            "& .MuiTablePagination-actions button": {
              padding: 1,
              width: 28,
              height: 28,
              minWidth: 28,
              minHeight: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#3F49A7",
              "&:hover": {
                backgroundColor: "#eceef5",
              },
              "&.Mui-disabled": {
                color: "#bbb",
              },
            },
            "& .MuiTablePagination-actions .MuiSvgIcon-root": {
              fontSize: "1rem",
            },
          }}
        />
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={
          activeFilter
            ? document.querySelector(`[data-filter-id="${activeFilter}"]`)
            : null
        }
        open={Boolean(activeFilter)}
        onClose={() => setActiveFilter(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            mt: 1,
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              left: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
      >
        <FilterPopoverContent>
          <FilterHeader>
            <Typography variant="subtitle2">
              Filter by{" "}
              {columnFilters.find((f) => f.id === activeFilter)?.label}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setFilterValues((prev) => ({ ...prev, [activeFilter]: [] }));
              }}
            >
              <ClearIcon />
            </IconButton>
          </FilterHeader>
          <FilterOptions>
            {getUniqueColumnValues(activeFilter).map((value) => (
              <MenuItem key={value} dense>
                <Checkbox
                  checked={filterValues[activeFilter]?.includes(value)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...(filterValues[activeFilter] || []), value]
                      : (filterValues[activeFilter] || []).filter(
                          (v) => v !== value
                        );
                    setFilterValues((prev) => ({
                      ...prev,
                      [activeFilter]: newValues,
                    }));
                  }}
                  size="small"
                />
                <ListItemText primary={value} />
              </MenuItem>
            ))}
          </FilterOptions>
        </FilterPopoverContent>
      </Menu>
    </TableWrapper>
  );
};
